import { omit } from 'lodash-es'
import { writable } from 'svelte/store'
import { newError } from '#app/lib/error'
import { objectEntries } from '#app/lib/utils'
import type { SerializedEntity } from '#entities/lib/entities'
import type { InvClaimValue } from '#server/types/entity'
import { i18n } from '#user/lib/i18n'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

export type EditorLabelValue = string | symbol
export type EditorLabels = Partial<Record<WikimediaLanguageCode, EditorLabelValue>>
export type EditorClaimValue <T extends InvClaimValue = InvClaimValue> = T | symbol
export type EditorSerializedEntity = SerializedEntity & { labels: EditorLabels }

export function getEditableLabels (labels: SerializedEntity['labels']) {
  return omit(labels, 'fromclaims')
}

export function alphabeticallySortedEntries (labels: EditorLabels) {
  return objectEntries(labels)
  .sort((a, b) => a[0] > b[0] ? 1 : -1)
}

export function getNonEmptyPropertyClaims (propertyClaims: EditorClaimValue[] = []) {
  return propertyClaims
  .filter(isNonEmptyClaimValue)
}

export function getPropertyClaimsCount (propertyClaims: EditorClaimValue[]) {
  return getNonEmptyPropertyClaims(propertyClaims).length
}

export function isEmptyClaimValue (value: EditorClaimValue) {
  return value === null || value === Symbol.for('removed') || value === Symbol.for('moved')
}

export const isNonEmptyClaimValue = (value: EditorClaimValue) => value != null && !isEmptyClaimValue(value)

export function addClaimValue (propertyClaims: EditorClaimValue[] = [], value: EditorClaimValue) {
  if (!propertyClaims.includes(value)) {
    propertyClaims.push(value)
  }
  return propertyClaims
}

export const currentEditorKey = writable(null)

export function errorMessageFormatter (err) {
  if (err.message.includes('this property value is already used')) {
    const uri = err.responseJSON.context.entity
    err.html = `${i18n('this property value is already used')}: <a href="/entity/${uri}" class="link">${uri}</a>`
  }
  if (err.statusCode === 413) {
    const cause = err
    err = newError('image is too big', 413)
    if (cause.stack) err.stack = cause.stack
    err.cause = cause
  }
  return err
}
